/**
 * EN / ES translations for the /privacy-policy and /terms pages.
 * Imported and merged into the main translations object.
 */

import { PHONE_DISPLAY as CONTACT_PHONE } from "../config/contact";
import { CONTACT_EMAIL } from "../lib/contact";

export interface LegalSection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
}

export interface LegalContent {
  title: string;
  intro: string;
  lastUpdatedLabel: string;
  lastUpdated: string;
  sections: LegalSection[];
}

const LAST_UPDATED_EN = "October 10, 2026";
const LAST_UPDATED_ES = "10 de octubre de 2026";

const privacyEn: LegalContent = {
  title: "Privacy Policy",
  lastUpdatedLabel: "Last updated",
  lastUpdated: LAST_UPDATED_EN,
  intro:
    "This Privacy Policy explains how Kranbos (\u201cKranbos\u201d, \u201cwe\u201d, \u201cus\u201d) collects, uses, and protects information when you visit kranbos.com, submit a form, or communicate with us by phone, text, or email.",
  sections: [
    {
      heading: "Information we collect",
      paragraphs: [
        "We only collect what we need to respond to you and to operate this website.",
      ],
      bullets: [
        "Information you give us: name, business name, email address, phone number, and any details you include in a form, call, text message, or email.",
        "Usage information: pages visited, referring page, approximate location, browser and device type, and similar analytics data collected automatically.",
        "Call and text information: calls to the number listed on this site may be answered by an AI voice agent operated through our voice AI provider. When you call or text us, we and that provider may collect your phone number, the date, time, and duration of the call, a recording and transcript of the conversation, and any details you share during it. The AI voice agent identifies itself as automated, and calls are recorded only where permitted by law and disclosed at the start of the call. You can ask to speak with a person at any time.",
      ],
    },
    {
      heading: "How we use information",
      bullets: [
        "To respond to your inquiry, answer and follow up on calls, schedule calls, and provide quotes and proposals.",
        "To deliver and support the services you engage us for.",
        "To send service updates and, if you opt in, occasional marketing messages you can unsubscribe from at any time.",
        "To measure which pages and campaigns bring in inquiries, and to improve the site.",
        "To meet legal, accounting, and security obligations, and to prevent spam and abuse.",
      ],
    },
    {
      heading: "Cookies and tracking",
      paragraphs: [
        "We use cookies and similar technologies for basic site functionality, analytics, and attribution of inquiries to the page or campaign they came from. You can block or delete cookies in your browser settings; some parts of the site may not work as intended if you do.",
      ],
    },
    {
      heading: "Service providers we share data with",
      paragraphs: [
        "We do not sell your personal information. We share it only with providers that help us run the business, and only to the extent they need it. These currently include our CRM and form-processing provider, our voice AI and telephony provider, our scheduling and payment providers, our website hosting and analytics providers, and our email provider. We may also disclose information when required by law or to protect our rights.",
      ],
    },
    {
      heading: "Text messaging",
      paragraphs: [
        "If you check the text-message consent box on one of our forms, Kranbos will send transactional and informational text messages to the mobile number you provide about your inquiry and the services you request, such as scheduling, follow-ups, and service updates. Consent to receive text messages is optional and is not a condition of purchase. Message frequency will vary. Message and data rates may apply. Reply HELP for help or STOP to opt out at any time, or email us at " +
          CONTACT_EMAIL +
          ".",
        "No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. All of the categories of sharing described in this policy exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.",
      ],
    },
    {
      heading: "Data retention",
      paragraphs: [
        "We keep inquiry and client records for as long as needed to provide our services and to meet legal and accounting requirements, then delete or anonymize them. You may ask us to delete your information sooner.",
      ],
    },
    {
      heading: "Security",
      paragraphs: [
        "We use reasonable technical and organizational safeguards, including encrypted connections and access controls, to protect the information we hold. No method of transmission or storage is completely secure, so we cannot guarantee absolute security.",
      ],
    },
    {
      heading: "Your choices and rights",
      paragraphs: [
        "You may request access to, correction of, or deletion of your personal information, ask us to stop marketing contact, or withdraw consent where we rely on it. Depending on where you live, you may have additional rights under laws such as the Florida Digital Bill of Rights, the California Consumer Privacy Act, or the GDPR. To make a request, email us at " +
          CONTACT_EMAIL +
          ". We will not discriminate against you for exercising these rights.",
      ],
    },
    {
      heading: "Children\u2019s privacy",
      paragraphs: [
        "Our services are intended for businesses. We do not knowingly collect personal information from anyone under 16. If you believe a child has provided us information, contact us and we will delete it.",
      ],
    },
    {
      heading: "Third-party links",
      paragraphs: [
        "This site links to third-party tools such as scheduling and payment pages. Those services have their own privacy policies, and we are not responsible for their practices.",
      ],
    },
    {
      heading: "Changes to this policy",
      paragraphs: [
        "We may update this Privacy Policy from time to time. The date above reflects the most recent version, and material changes will be posted on this page.",
      ],
    },
    {
      heading: "Contact us",
      paragraphs: [
        "Questions about this policy or your information? Email " +
          CONTACT_EMAIL +
          " or call " +
          CONTACT_PHONE +
          ". Kranbos is based in Miami, Florida, United States.",
      ],
    },
  ],
};

const termsEn: LegalContent = {
  title: "Terms & Conditions",
  lastUpdatedLabel: "Last updated",
  lastUpdated: LAST_UPDATED_EN,
  intro:
    "These Terms & Conditions govern your use of kranbos.com and any information or forms you submit through it. By using this website you agree to these terms.",
  sections: [
    {
      heading: "Use of this website",
      paragraphs: [
        "You may use this site for lawful purposes only. You agree not to interfere with the site, attempt to gain unauthorized access to it, scrape it at a scale that degrades service, submit false information, or use our forms or phone numbers to send spam or unsolicited offers.",
      ],
    },
    {
      heading: "No professional advice",
      paragraphs: [
        "The content on this site, including pages about lead recovery, automation, and reporting, is general information about our services. It is not legal, tax, accounting, or other professional advice, and results described are examples rather than promises about your business.",
      ],
    },
    {
      heading: "Inquiries and quotes",
      paragraphs: [
        "Submitting a form, booking a call, or requesting a quote does not create a binding agreement. Pricing, scope, and timelines shown on the site are indicative and are confirmed only in a written proposal or agreement signed or accepted by both parties.",
      ],
    },
    {
      heading: "Services and client agreements",
      paragraphs: [
        "Paid engagements are governed by a separate proposal, statement of work, or service agreement. If anything in that agreement conflicts with these terms, that agreement controls for the services it covers. Our work often depends on third-party platforms such as your CRM, phone system, or messaging provider, and on your timely access, approvals, and content.",
      ],
    },
    {
      heading: "Fees and payment",
      paragraphs: [
        "Fees, billing frequency, and payment terms are set out in your agreement with us. Unless stated otherwise there, invoices are due on receipt, setup fees are non-refundable once work has started, and recurring services may be paused for non-payment. Recurring plans can be cancelled with the notice period stated in your agreement.",
      ],
    },
    {
      heading: "Intellectual property",
      paragraphs: [
        "The Kranbos name, logo, site design, and content are owned by Kranbos and may not be copied or reused without written permission. Deliverables produced for a client are licensed or assigned to that client as described in their agreement, while the underlying tools, templates, and know-how we reuse across clients remain ours. You keep ownership of the content and data you provide to us.",
      ],
    },
    {
      heading: "Third-party services",
      paragraphs: [
        "The site links to and integrates with third-party services such as scheduling, payment, CRM, and voice AI and telephony providers. Their terms apply when you use them, and we are not responsible for their availability, pricing changes, or actions.",
      ],
    },
    {
      heading: "Disclaimer of warranties",
      paragraphs: [
        "This website is provided \u201cas is\u201d and \u201cas available\u201d without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the site will be uninterrupted, error free, or free of harmful components.",
      ],
    },
    {
      heading: "Limitation of liability",
      paragraphs: [
        "To the fullest extent permitted by law, Kranbos is not liable for indirect, incidental, special, consequential, or punitive damages, or for lost profits, revenue, or data arising from your use of this website. Our total liability relating to the website is limited to one hundred U.S. dollars (US$100). Liability for paid services is limited as stated in the applicable service agreement.",
      ],
    },
    {
      heading: "Communications consent",
      paragraphs: [
        "When you submit a form or call a number on this site, you agree that we may contact you by phone and email about your inquiry. We send text messages only if you check the separate text-message consent box on our forms. See our Privacy Policy for details on how we handle that information and how to opt out.",
      ],
    },
    {
      heading: "Text messaging (SMS) terms",
      bullets: [
        "Kranbos sends transactional and informational text messages about your inquiry, scheduling, and the services you request to people who opt in through the text-message consent box on our website forms.",
        "Opting in is optional and is not a condition of purchase.",
        "Message frequency will vary. Message and data rates may apply.",
        "Reply STOP at any time to stop receiving texts. You will receive one message confirming your opt-out and no further texts unless you opt in again.",
        "Reply HELP for help, or contact us at " + CONTACT_EMAIL + " or " + CONTACT_PHONE + ".",
        "Carriers are not liable for delayed or undelivered messages.",
        "See our Privacy Policy for how we handle your information. Mobile opt-in data and consent are never shared with third parties.",
      ],
    },
    {
      heading: "Governing law",
      paragraphs: [
        "These terms are governed by the laws of the State of Florida, United States, without regard to conflict-of-law rules. Any dispute relating to this website will be brought in the state or federal courts located in Miami-Dade County, Florida.",
      ],
    },
    {
      heading: "Changes to these terms",
      paragraphs: [
        "We may update these terms at any time. The date above reflects the most recent version, and continued use of the site after an update means you accept the revised terms.",
      ],
    },
    {
      heading: "Contact us",
      paragraphs: [
        "Questions about these terms? Email " +
          CONTACT_EMAIL +
          " or call " +
          CONTACT_PHONE +
          ".",
      ],
    },
  ],
};

const privacyEs: LegalContent = {
  title: "Pol\u00edtica de Privacidad",
  lastUpdatedLabel: "\u00daltima actualizaci\u00f3n",
  lastUpdated: LAST_UPDATED_ES,
  intro:
    "Esta Pol\u00edtica de Privacidad explica c\u00f3mo Kranbos (\u201cKranbos\u201d, \u201cnosotros\u201d) recopila, usa y protege la informaci\u00f3n cuando visitas kranbos.com, env\u00edas un formulario o te comunicas con nosotros por tel\u00e9fono, mensaje de texto o correo electr\u00f3nico.",
  sections: [
    {
      heading: "Informaci\u00f3n que recopilamos",
      paragraphs: [
        "Solo recopilamos lo necesario para responderte y para operar este sitio web.",
      ],
      bullets: [
        "Informaci\u00f3n que nos das: nombre, nombre del negocio, correo electr\u00f3nico, tel\u00e9fono y los detalles que incluyas en un formulario, llamada, mensaje de texto o correo.",
        "Informaci\u00f3n de uso: p\u00e1ginas visitadas, p\u00e1gina de referencia, ubicaci\u00f3n aproximada, tipo de navegador y dispositivo, y datos anal\u00edticos similares recopilados autom\u00e1ticamente.",
        "Informaci\u00f3n de llamadas y mensajes: las llamadas al n\u00famero listado en este sitio pueden ser atendidas por un agente de voz con IA operado a trav\u00e9s de nuestro proveedor de IA de voz. Cuando nos llamas o env\u00edas un mensaje, nosotros y ese proveedor podemos recopilar tu n\u00famero de tel\u00e9fono, la fecha, la hora y la duraci\u00f3n de la llamada, una grabaci\u00f3n y transcripci\u00f3n de la conversaci\u00f3n, y los datos que compartas durante ella. El agente de voz con IA se identifica como automatizado, y las llamadas se graban solo cuando la ley lo permite y se avisa al inicio de la llamada. Puedes pedir hablar con una persona en cualquier momento.",
      ],
    },
    {
      heading: "C\u00f3mo usamos la informaci\u00f3n",
      bullets: [
        "Para responder tu consulta, atender y dar seguimiento a llamadas, agendar llamadas y enviar presupuestos y propuestas.",
        "Para entregar y dar soporte a los servicios que contrates con nosotros.",
        "Para enviarte novedades del servicio y, si lo autorizas, mensajes de marketing ocasionales de los que puedes darte de baja en cualquier momento.",
        "Para medir qu\u00e9 p\u00e1ginas y campa\u00f1as generan consultas y mejorar el sitio.",
        "Para cumplir obligaciones legales, contables y de seguridad, y para prevenir spam y abusos.",
      ],
    },
    {
      heading: "Cookies y seguimiento",
      paragraphs: [
        "Usamos cookies y tecnolog\u00edas similares para el funcionamiento b\u00e1sico del sitio, anal\u00edtica y atribuci\u00f3n de consultas a la p\u00e1gina o campa\u00f1a de origen. Puedes bloquear o borrar las cookies desde tu navegador; algunas partes del sitio pueden no funcionar correctamente si lo haces.",
      ],
    },
    {
      heading: "Proveedores con los que compartimos datos",
      paragraphs: [
        "No vendemos tu informaci\u00f3n personal. Solo la compartimos con proveedores que nos ayudan a operar el negocio y \u00fanicamente en la medida en que la necesitan. Actualmente incluyen nuestro proveedor de CRM y procesamiento de formularios, nuestro proveedor de IA de voz y telefon\u00eda, nuestros proveedores de agenda y pagos, nuestros proveedores de hosting y anal\u00edtica, y nuestro proveedor de correo. Tambi\u00e9n podemos divulgar informaci\u00f3n cuando la ley lo exige o para proteger nuestros derechos.",
      ],
    },
    {
      heading: "Mensajes de texto",
      paragraphs: [
        "Si marcas la casilla de consentimiento de mensajes de texto en uno de nuestros formularios, Kranbos enviar\u00e1 mensajes de texto transaccionales e informativos al n\u00famero m\u00f3vil que proporciones sobre tu consulta y los servicios que solicitas, como agenda, seguimientos y novedades del servicio. El consentimiento para recibir mensajes de texto es opcional y no es condici\u00f3n de compra. La frecuencia de los mensajes variar\u00e1. Pueden aplicarse tarifas de mensajes y datos. Responde HELP para obtener ayuda o STOP para darte de baja en cualquier momento, o escr\u00edbenos a " +
          CONTACT_EMAIL +
          ".",
        "No compartiremos informaci\u00f3n m\u00f3vil con terceros ni afiliados con fines de marketing o promocionales. Todas las categor\u00edas de intercambio descritas en esta pol\u00edtica excluyen los datos de consentimiento (opt-in) para mensajes de texto; esta informaci\u00f3n no se compartir\u00e1 con ning\u00fan tercero.",
      ],
    },
    {
      heading: "Conservaci\u00f3n de datos",
      paragraphs: [
        "Conservamos los registros de consultas y clientes durante el tiempo necesario para prestar nuestros servicios y cumplir requisitos legales y contables, y luego los eliminamos o anonimizamos. Puedes pedirnos que eliminemos tu informaci\u00f3n antes.",
      ],
    },
    {
      heading: "Seguridad",
      paragraphs: [
        "Aplicamos medidas t\u00e9cnicas y organizativas razonables, incluidas conexiones cifradas y controles de acceso, para proteger la informaci\u00f3n que tenemos. Ning\u00fan m\u00e9todo de transmisi\u00f3n o almacenamiento es completamente seguro, por lo que no podemos garantizar seguridad absoluta.",
      ],
    },
    {
      heading: "Tus opciones y derechos",
      paragraphs: [
        "Puedes solicitar acceso, correcci\u00f3n o eliminaci\u00f3n de tu informaci\u00f3n personal, pedirnos que dejemos de contactarte con fines de marketing o retirar tu consentimiento cuando nos basemos en \u00e9l. Seg\u00fan d\u00f3nde vivas, puedes tener derechos adicionales bajo leyes como la Florida Digital Bill of Rights, la California Consumer Privacy Act o el GDPR. Para hacer una solicitud, escr\u00edbenos a " +
          CONTACT_EMAIL +
          ". No te discriminaremos por ejercer estos derechos.",
      ],
    },
    {
      heading: "Privacidad de menores",
      paragraphs: [
        "Nuestros servicios est\u00e1n dirigidos a negocios. No recopilamos a sabiendas informaci\u00f3n personal de menores de 16 a\u00f1os. Si crees que un menor nos proporcion\u00f3 informaci\u00f3n, cont\u00e1ctanos y la eliminaremos.",
      ],
    },
    {
      heading: "Enlaces a terceros",
      paragraphs: [
        "Este sitio enlaza a herramientas de terceros como p\u00e1ginas de agenda y pagos. Esos servicios tienen sus propias pol\u00edticas de privacidad y no somos responsables de sus pr\u00e1cticas.",
      ],
    },
    {
      heading: "Cambios en esta pol\u00edtica",
      paragraphs: [
        "Podemos actualizar esta Pol\u00edtica de Privacidad ocasionalmente. La fecha indicada arriba corresponde a la versi\u00f3n m\u00e1s reciente, y los cambios relevantes se publicar\u00e1n en esta p\u00e1gina.",
      ],
    },
    {
      heading: "Contacto",
      paragraphs: [
        "\u00bfPreguntas sobre esta pol\u00edtica o tu informaci\u00f3n? Escribe a " +
          CONTACT_EMAIL +
          " o llama al " +
          CONTACT_PHONE +
          ". Kranbos opera desde Miami, Florida, Estados Unidos.",
      ],
    },
  ],
};

const termsEs: LegalContent = {
  title: "T\u00e9rminos y Condiciones",
  lastUpdatedLabel: "\u00daltima actualizaci\u00f3n",
  lastUpdated: LAST_UPDATED_ES,
  intro:
    "Estos T\u00e9rminos y Condiciones regulan el uso de kranbos.com y de la informaci\u00f3n o formularios que env\u00edes a trav\u00e9s del sitio. Al usar este sitio web aceptas estos t\u00e9rminos.",
  sections: [
    {
      heading: "Uso de este sitio web",
      paragraphs: [
        "Puedes usar este sitio \u00fanicamente para fines legales. Aceptas no interferir con el sitio, no intentar accesos no autorizados, no extraer datos a una escala que degrade el servicio, no enviar informaci\u00f3n falsa y no usar nuestros formularios o n\u00fameros de tel\u00e9fono para enviar spam u ofertas no solicitadas.",
      ],
    },
    {
      heading: "No es asesoramiento profesional",
      paragraphs: [
        "El contenido de este sitio, incluidas las p\u00e1ginas sobre recuperaci\u00f3n de leads, automatizaci\u00f3n y reportes, es informaci\u00f3n general sobre nuestros servicios. No constituye asesoramiento legal, fiscal, contable ni de otro tipo profesional, y los resultados descritos son ejemplos, no promesas sobre tu negocio.",
      ],
    },
    {
      heading: "Consultas y presupuestos",
      paragraphs: [
        "Enviar un formulario, agendar una llamada o solicitar un presupuesto no crea un acuerdo vinculante. Los precios, alcances y plazos mostrados en el sitio son indicativos y se confirman solo en una propuesta o acuerdo escrito firmado o aceptado por ambas partes.",
      ],
    },
    {
      heading: "Servicios y acuerdos con clientes",
      paragraphs: [
        "Los trabajos pagos se rigen por una propuesta, orden de trabajo o acuerdo de servicio por separado. Si algo en ese acuerdo entra en conflicto con estos t\u00e9rminos, prevalece ese acuerdo para los servicios que cubre. Nuestro trabajo suele depender de plataformas de terceros como tu CRM, sistema telef\u00f3nico o proveedor de mensajer\u00eda, y de que nos brindes accesos, aprobaciones y contenidos a tiempo.",
      ],
    },
    {
      heading: "Honorarios y pagos",
      paragraphs: [
        "Los honorarios, la frecuencia de facturaci\u00f3n y las condiciones de pago se establecen en tu acuerdo con nosotros. Salvo que ese acuerdo indique lo contrario, las facturas se pagan al recibirlas, los cargos de instalaci\u00f3n no son reembolsables una vez iniciado el trabajo y los servicios recurrentes pueden pausarse por falta de pago. Los planes recurrentes pueden cancelarse con el aviso previo indicado en tu acuerdo.",
      ],
    },
    {
      heading: "Propiedad intelectual",
      paragraphs: [
        "El nombre Kranbos, su logo, el dise\u00f1o del sitio y su contenido son propiedad de Kranbos y no pueden copiarse ni reutilizarse sin permiso escrito. Los entregables producidos para un cliente se licencian o ceden a ese cliente seg\u00fan su acuerdo, mientras que las herramientas, plantillas y conocimientos que reutilizamos entre clientes siguen siendo nuestros. T\u00fa conservas la propiedad del contenido y los datos que nos proporcionas.",
      ],
    },
    {
      heading: "Servicios de terceros",
      paragraphs: [
        "El sitio enlaza e integra servicios de terceros como proveedores de agenda, pagos, CRM, e IA de voz y telefon\u00eda. Sus t\u00e9rminos aplican cuando los usas y no somos responsables de su disponibilidad, cambios de precio ni acciones.",
      ],
    },
    {
      heading: "Exclusi\u00f3n de garant\u00edas",
      paragraphs: [
        "Este sitio web se ofrece \u201ctal cual\u201d y \u201cseg\u00fan disponibilidad\u201d, sin garant\u00edas de ning\u00fan tipo, expresas o impl\u00edcitas, incluidas las de comerciabilidad, idoneidad para un fin determinado y no infracci\u00f3n. No garantizamos que el sitio funcione sin interrupciones, sin errores ni libre de componentes da\u00f1inos.",
      ],
    },
    {
      heading: "Limitaci\u00f3n de responsabilidad",
      paragraphs: [
        "En la m\u00e1xima medida permitida por la ley, Kranbos no es responsable por da\u00f1os indirectos, incidentales, especiales, consecuentes o punitivos, ni por p\u00e9rdida de ganancias, ingresos o datos derivados del uso de este sitio web. Nuestra responsabilidad total relacionada con el sitio se limita a cien d\u00f3lares estadounidenses (US$100). La responsabilidad por servicios pagos se limita seg\u00fan el acuerdo de servicio aplicable.",
      ],
    },
    {
      heading: "Consentimiento de comunicaciones",
      paragraphs: [
        "Al enviar un formulario o llamar a un n\u00famero de este sitio, aceptas que podamos contactarte por tel\u00e9fono y correo electr\u00f3nico sobre tu consulta. Solo enviamos mensajes de texto si marcas la casilla separada de consentimiento de mensajes de texto en nuestros formularios. Consulta nuestra Pol\u00edtica de Privacidad para saber c\u00f3mo tratamos esa informaci\u00f3n y c\u00f3mo darte de baja.",
      ],
    },
    {
      heading: "T\u00e9rminos de mensajes de texto (SMS)",
      bullets: [
        "Kranbos env\u00eda mensajes de texto transaccionales e informativos sobre tu consulta, la agenda y los servicios que solicitas a quienes dan su consentimiento mediante la casilla de mensajes de texto de nuestros formularios web.",
        "Dar tu consentimiento es opcional y no es condici\u00f3n de compra.",
        "La frecuencia de los mensajes variar\u00e1. Pueden aplicarse tarifas de mensajes y datos.",
        "Responde STOP en cualquier momento para dejar de recibir mensajes. Recibir\u00e1s un mensaje confirmando tu baja y ning\u00fan otro mensaje, salvo que vuelvas a darte de alta.",
        "Responde HELP para obtener ayuda, o cont\u00e1ctanos en " + CONTACT_EMAIL + " o al " + CONTACT_PHONE + ".",
        "Los operadores no son responsables de mensajes demorados o no entregados.",
        "Consulta nuestra Pol\u00edtica de Privacidad para saber c\u00f3mo tratamos tu informaci\u00f3n. Los datos de consentimiento m\u00f3vil nunca se comparten con terceros.",
      ],
    },
    {
      heading: "Ley aplicable",
      paragraphs: [
        "Estos t\u00e9rminos se rigen por las leyes del Estado de Florida, Estados Unidos, sin considerar sus normas de conflicto de leyes. Cualquier disputa relacionada con este sitio web se someter\u00e1 a los tribunales estatales o federales del condado de Miami-Dade, Florida.",
      ],
    },
    {
      heading: "Cambios en estos t\u00e9rminos",
      paragraphs: [
        "Podemos actualizar estos t\u00e9rminos en cualquier momento. La fecha indicada arriba corresponde a la versi\u00f3n m\u00e1s reciente, y el uso continuado del sitio despu\u00e9s de una actualizaci\u00f3n implica que aceptas los t\u00e9rminos revisados.",
      ],
    },
    {
      heading: "Contacto",
      paragraphs: [
        "\u00bfPreguntas sobre estos t\u00e9rminos? Escribe a " +
          CONTACT_EMAIL +
          " o llama al " +
          CONTACT_PHONE +
          ".",
      ],
    },
  ],
};

export const legalTranslations = {
  en: {
    privacy: privacyEn,
    terms: termsEn,
  },
  es: {
    privacy: privacyEs,
    terms: termsEs,
  },
};
